<template>
  <div class="invoice-preview-panel">
    <div class="d-flex align-center mb-4">
      <div class="text-h6 font-weight-bold">
        {{ previewTitle }}
      </div>

      <v-spacer />

      <v-chip small outlined color="orange">
        {{ previewTitle }}
      </v-chip>
    </div>

    <div class="preview-sheet">
      <div class="d-flex justify-space-between align-start flex-wrap mb-6">
        <div class="mb-4 mr-8 preview-block">
          <div class="text-subtitle-1 font-weight-bold mb-2">
            {{ t.seller }}
          </div>

          <div class="font-weight-medium">
            {{ sellerData.name || '-' }}
          </div>
          <div v-if="sellerData.address">{{ sellerData.address }}</div>
          <div v-if="sellerData.taxNumber">{{ t.taxNumber }}: {{ sellerData.taxNumber }}</div>
          <div v-if="sellerData.email">{{ t.email }}: {{ sellerData.email }}</div>
          <div v-if="sellerData.phone">{{ t.phone }}: {{ sellerData.phone }}</div>
          <div v-if="sellerData.bankName">{{ t.bankName }}: {{ sellerData.bankName }}</div>
          <div v-if="sellerData.bankAccount">
            {{ sellerData.isIban ? t.iban : t.bankAccount }}: {{ sellerData.bankAccount }}
          </div>
        </div>

        <div class="mb-4 mr-8 preview-block">
          <div class="text-subtitle-1 font-weight-bold mb-2">
            {{ t.buyer }}
          </div>

          <div class="font-weight-medium">
            {{ buyerData.name || '-' }}
          </div>
          <div v-if="buyerData.address">{{ buyerData.address }}</div>
          <div v-if="buyerData.taxNumber">{{ t.taxNumber }}: {{ buyerData.taxNumber }}</div>
          <div v-if="buyerData.email">{{ t.email }}: {{ buyerData.email }}</div>
          <div v-if="buyerData.phone">{{ t.phone }}: {{ buyerData.phone }}</div>
        </div>

        <div class="mb-4 preview-meta">
          <div class="mb-1">
            <strong>{{ t.type }}:</strong> {{ previewTitle }}
          </div>

          <div v-if="documentData.issueDate" class="mb-1">
            <strong>{{ t.issueDate }}:</strong> {{ formatDate(documentData.issueDate) }}
          </div>

          <div v-if="documentData.fulfillmentDate" class="mb-1">
            <strong>{{ t.fulfillmentDate }}:</strong> {{ formatDate(documentData.fulfillmentDate) }}
          </div>

          <div v-if="documentData.paymentDeadline" class="mb-1">
            <strong>{{ t.paymentDeadline }}:</strong> {{ formatDate(documentData.paymentDeadline) }}
          </div>

          <div v-if="documentData.paymentMethod" class="mb-1">
            <strong>{{ t.paymentMethod }}:</strong> {{ documentData.paymentMethod }}
          </div>

          <div v-if="documentData.orderNumber" class="mb-1">
            <strong>{{ t.orderNumber }}:</strong> {{ documentData.orderNumber }}
          </div>

          <div
            v-if="documentData.proformaNumber && documentType === 'final_invoice'"
            class="mb-1"
          >
            <strong>{{ t.relatedProforma }}:</strong> {{ documentData.proformaNumber }}
          </div>

          <div class="mb-1">
            <strong>{{ t.currency }}:</strong> {{ documentCurrency }}
          </div>

          <div v-if="hasZeroTaxOrder" class="mb-1">
            <strong>{{ t.taxMode }}:</strong> {{ t.vatExempt }}
          </div>
        </div>
      </div>

      <v-simple-table class="mb-6">
        <thead>
          <tr>
            <th class="text-left">#</th>
            <th class="text-left">{{ t.itemName }}</th>
            <th class="text-right">{{ t.quantity }}</th>
            <th class="text-left">{{ t.unit }}</th>
            <th class="text-right">{{ t.unitPriceNet }}</th>
            <th class="text-right">{{ t.vat }}</th>
            <th class="text-right">{{ t.net }}</th>
            <th class="text-right">{{ t.vatAmount }}</th>
            <th class="text-right">{{ t.gross }}</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, index) in previewItems"
            :key="index"
            :class="{ 'advance-deduction-row': item.isAdvanceDeduction }"
          >
            <td>{{ index + 1 }}</td>

            <td>
              <div>{{ item.name }}</div>

              <small
                v-if="item.reference"
                class="d-block grey--text text--darken-1"
              >
                {{ t.reference }}: {{ item.reference }}
              </small>

              <small
                v-if="item.note"
                class="d-block grey--text text--darken-1 item-comment"
              >
                {{ item.note }}
              </small>
            </td>

            <td class="text-right">{{ formatQuantity(item.quantity) }}</td>
            <td>{{ item.unit }}</td>
            <td class="text-right">{{ formatMoney(item.unitPriceNet) }}</td>
            <td class="text-right">{{ item.vatDisplay }}</td>
            <td class="text-right">{{ formatMoney(item.netAmount) }}</td>
            <td class="text-right">{{ formatMoney(item.vatAmount) }}</td>
            <td class="text-right">{{ formatMoney(item.grossAmount) }}</td>
          </tr>

          <tr v-if="!previewItems.length">
            <td colspan="9" class="text-center grey--text text--darken-1 py-6">
              {{ t.noItems }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <div v-if="vatSummary.length" class="mb-6">
        <div class="font-weight-bold mb-2">
          {{ t.vatSummary }}
        </div>

        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">{{ t.vat }}</th>
              <th class="text-right">{{ t.net }}</th>
              <th class="text-right">{{ t.vatAmount }}</th>
              <th class="text-right">{{ t.gross }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, index) in vatSummary" :key="index">
              <td>{{ row.vat }}</td>
              <td class="text-right">{{ formatMoney(row.net) }}</td>
              <td class="text-right">{{ formatMoney(row.vatAmount) }}</td>
              <td class="text-right">{{ formatMoney(row.gross) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div class="d-flex justify-end mb-6">
        <div class="totals-box">
          <div v-if="hasOrderDiscount" class="d-flex justify-space-between mb-2">
            <span>{{ t.subtotalNet }}:</span>
            <strong>{{ formatMoney(originalOrderNetTotal) }}</strong>
          </div>

          <div v-if="hasOrderDiscount" class="d-flex justify-space-between mb-2 discount-row">
            <span>{{ t.discount }}:</span>
            <strong>{{ formatMoney(-orderDiscountNetAmount) }}</strong>
          </div>

          <div class="d-flex justify-space-between mb-2">
            <span>{{ t.totalNet }}:</span>
            <strong>{{ formatMoney(totalNet) }}</strong>
          </div>

          <div class="d-flex justify-space-between mb-2">
            <span>{{ t.totalVat }}:</span>
            <strong>{{ formatMoney(totalVat) }}</strong>
          </div>

          <div class="d-flex justify-space-between text-h6 font-weight-bold">
            <span>{{ t.totalGross }}:</span>
            <span>{{ formatMoney(totalGross) }}</span>
          </div>
        </div>
      </div>

      <div v-if="documentData.note" class="mb-4">
        <div class="font-weight-bold mb-1">
          {{ t.note }}
        </div>

        <div class="note-box">
          {{ documentData.note }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InvoicePreviewPanel',

  props: {
    documentType: {
      type: String,
      default: 'invoice', // proforma | invoice | final_invoice
    },
    order: {
      type: Object,
      default: () => ({}),
    },
    settings: {
      type: Object,
      default: () => ({
        type: null,
        mode: null,
        percent: null,
        value: null,
        vat: null,
        fulfillmentDate: null,
        paymentDeadline: null,
        note: null,
        issueDate: null,
        paymentMethod: null,
        proformaNumber: null,
        is_paid: false,
      }),
    },
    seller: {
      type: Object,
      default: () => ({
        name: 'Kismetál Kft.',
        address: null,
        taxNumber: null,
        email: null,
        phone: null,
        bankAccount: null,
      }),
    },
    currency: {
      type: String,
      default: 'HUF',
    },
  },

  computed: {
    currentLocale() {
      const locale = String(this.order?.locale || 'hu').toLowerCase()

      if (['hu', 'en', 'ro'].includes(locale)) {
        return locale
      }

      return 'hu'
    },

    t() {
      return this.translations[this.currentLocale]
    },

    previewTitle() {
      if (this.documentType === 'proforma') return this.t.proformaPreview
      if (this.documentType === 'final_invoice') return this.t.finalInvoicePreview
      if (this.documentType === 'deposit_invoice') return this.t.depositInvoicePreview
      return this.t.invoicePreview
    },

    orderCustomFields() {
      const customFields = this.order?.custom_fields

      if (!customFields) {
        return {}
      }

      if (typeof customFields === 'object') {
        return customFields
      }

      try {
        return JSON.parse(customFields) || {}
      } catch (e) {
        return {}
      }
    },

    selectedBank() {
      const bank = this.orderCustomFields?.bank

      if (!bank) {
        return {}
      }

      if (typeof bank === 'object') {
        return bank
      }

      try {
        return JSON.parse(bank) || {}
      } catch (e) {
        return {}
      }
    },

    documentCurrency() {
      const currency =
        this.order?.priceList?.currency_id ??
        this.order?.price_list?.currency_id ??
        this.order?.currency_id ??
        this.order?.currency ??
        this.currency ??
        'HUF'

      return String(currency).trim().toUpperCase() || 'HUF'
    },

    sellerData() {
      const seller = this.seller || {}
      const bank = this.selectedBank
      const selectedBankName = String(bank?.name || '').trim()
      const selectedBankAccount = String(
        this.documentCurrency === 'EUR'
          ? bank?.iban || ''
          : bank?.bank_account_number || ''
      ).trim()

      // Csak akkor írjuk felül az alapértelmezett eladói bankadatokat,
      // ha az adott pénznemhez tartozó számlaszám ténylegesen rendelkezésre áll.
      const hasBankOverride = selectedBankAccount !== ''

      return {
        ...seller,
        bankName: hasBankOverride
          ? selectedBankName
          : seller.bankName || seller.bank_name || null,
        bankAccount: hasBankOverride
          ? selectedBankAccount
          : seller.bankAccount || seller.bank_account || null,
        isIban: hasBankOverride
          ? this.documentCurrency === 'EUR'
          : Boolean(seller.isIban || seller.is_iban),
      }
    },

    buyerData() {
      const customer =
        this.order?.customer ||
        this.order?.partner ||
        this.order?.contact ||
        {}
      
      const customerAddress = this.order?.addressess?.billingAddress.name;

      return {
        name:
          customer.companyName ||
          customer.name ||
          customer.companyName ||
          this.order?.customer_name ||
          this.order?.partner_name ||
          null,
        address:
          customerAddress ||
          customer.address ||
          customer.full_address ||
          customer.invoice_address ||
          this.order?.customer_address ||
          this.order?.partner_address ||
          null,
        taxNumber:
          customer.vat_number ||
          customer.tax_number ||
          customer.taxNumber ||
          null,
        email: customer.email || null,
        phone: customer?.contact_data.phone_number || null,
      }
    },

    documentData() {
      /*let orderComments = this.order?.documents?.map(doc => doc.type === 'deposit_invoice' ? doc.comment : null).join('\n');
      let uniqComments = [...new Set(orderComments.split('\n').filter(c => c))].join('\n');*/

      return {
        issueDate: this.settings?.issueDate || this.getTodayDate(),
        fulfillmentDate: this.settings?.fulfillmentDate || new Date(),
        paymentDeadline: this.settings?.paymentDeadline || new Date(Date.now() + 8 * 24 * 60 * 60 * 1000),
        paymentMethod: this.normalizePaymentMethod(
          this.order?.locale,
          this.resolveDocumentPaymentMethod()
        ) || null,
        orderNumber:
          this.order?.so_number ||
          this.order?.order_number ||
          this.order?.number ||
          this.order?.reference ||
          this.order?.id ||
          null,
        proformaNumber: this.settings?.proformaNumber || null,
        note: this.resolveComments(),
      }
    },

    hasZeroTaxOrder() {
      const zeroTax = this.order?.zero_tax

      return zeroTax === true || zeroTax === 1 || zeroTax === '1'
    },

    hasReverseTaxOrder() {
      const reverseTax = this.order?.reverse_tax

      return reverseTax === true || reverseTax === 1 || reverseTax === '1'
    },

    rawOrderItems() {
      if (Array.isArray(this.order?.items)) return this.order.items
      if (Array.isArray(this.order?.sales_order_items)) return this.order.sales_order_items
      if (Array.isArray(this.order?.order_items)) return this.order.order_items
      if (Array.isArray(this.order?.invoice_items)) return this.order.invoice_items

      return []
    },

    originalLineNets() {
      return this.rawOrderItems.map(item => {
        const quantity = this.resolveQuantity(item)
        return this.roundMoney(this.resolveLineNet(item, quantity))
      })
    },

    discountedLineNets() {
      const originalLineNets = this.originalLineNets

      if (!originalLineNets.length) {
        return []
      }

      const hasTargetTotalNet = ![
        undefined,
        null,
        '',
      ].includes(
        this.order?.total_net ??
        this.order?.net_total ??
        this.order?.netTotal
      )

      if (!hasTargetTotalNet) {
        return originalLineNets
      }

      const originalTotalNet = this.roundMoney(
        originalLineNets.reduce((sum, value) => sum + this.toNumber(value), 0)
      )
      const targetTotalNet = this.roundMoney(
        this.order?.total_net ??
        this.order?.net_total ??
        this.order?.netTotal ??
        0
      )

      if (originalTotalNet <= 0 || Math.abs(originalTotalNet - targetTotalNet) < 0.01) {
        return originalLineNets
      }

      const ratio = targetTotalNet / originalTotalNet
      const discountedLineNets = []
      let allocated = 0
      const lastIndex = originalLineNets.length - 1

      originalLineNets.forEach((originalLineNet, index) => {
        let lineNet

        if (index === lastIndex) {
          lineNet = this.roundMoney(targetTotalNet - allocated)
        } else {
          lineNet = this.roundMoney(originalLineNet * ratio)
          allocated = this.roundMoney(allocated + lineNet)
        }

        discountedLineNets.push(lineNet)
      })

      return discountedLineNets
    },

    originalOrderNetTotal() {
      return this.roundMoney(
        this.originalLineNets.reduce((sum, value) => sum + this.toNumber(value), 0)
      )
    },

    orderDiscountNetAmount() {
      return this.roundMoney(
        Math.max(0, this.originalOrderNetTotal - this.orderNetTotal)
      )
    },

    hasOrderDiscount() {
      return this.orderDiscountNetAmount > 0.009
    },

    normalizedOrderItems() {
      return this.rawOrderItems.map((item, index) => {
        const quantity = this.resolveQuantity(item)
        const vatRaw = this.resolveEffectiveVat(item)
        const vatExempt = this.hasZeroTaxOrder || this.isVatExempt(vatRaw)

        const originalNetAmount = this.roundMoney(this.resolveLineNet(item, quantity))
        const netAmount = this.roundMoney(
          this.discountedLineNets[index] ?? originalNetAmount
        )
        const lineNetAdjusted = !this.isAlmostEqual(netAmount, originalNetAmount)

        const vatAmount = this.roundMoney(
          vatExempt
            ? 0
            : lineNetAdjusted
              ? this.calculateVatAmount(netAmount, vatRaw)
              : this.resolveLineVat(item, netAmount, vatRaw)
        )

        const grossAmount = this.roundMoney(
          vatExempt
            ? netAmount
            : lineNetAdjusted
              ? netAmount + vatAmount
              : this.resolveLineGross(item, netAmount, vatAmount)
        )

        const unitPriceNet = quantity > 0
          ? this.roundMoney(netAmount / quantity)
          : netAmount

        return {
          name: this.resolveItemName(item),
          note: this.resolveItemComment(item, quantity),
          reference: item.reference || item.item?.reference || null,
          quantity,
          unit: this.resolveUnit(item),
          unitPriceNet,
          vat: vatRaw,
          vatDisplay: this.resolveVatDisplay(vatRaw, vatExempt),
          netAmount,
          vatAmount,
          grossAmount,
        }
      })
    },

    orderNetTotal() {
      if (this.normalizedOrderItems.length) {
        return this.normalizedOrderItems.reduce((sum, item) => sum + Number(item.netAmount || 0), 0)
      }

      return this.toNumber(
        this.order?.netTotal ??
        this.order?.net_total ??
        this.order?.total_net ??
        0
      )
    },

    previewItems() {
      if (this.documentType === 'proforma') {
        return this.buildProformaItems()
      }

      if (this.documentType === 'deposit_invoice') {
        return this.buildProformaItems()
      }

      if (this.documentType === 'final_invoice') {
        return this.buildFinalInvoiceItems()
      }

      return this.normalizedOrderItems
    },

    totalNet() {
      return this.previewItems.reduce((sum, item) => sum + Number(item.netAmount || 0), 0)
    },

    totalVat() {
      return this.previewItems.reduce((sum, item) => sum + Number(item.vatAmount || 0), 0)
    },

    totalGross() {
      return this.previewItems.reduce((sum, item) => sum + Number(item.grossAmount || 0), 0)
    },

    vatSummary() {
      const grouped = {}

      this.previewItems.forEach(item => {
        const key = item.vatDisplay || '-'

        if (!grouped[key]) {
          grouped[key] = {
            vat: item.vatDisplay || '-',
            net: 0,
            vatAmount: 0,
            gross: 0,
          }
        }

        grouped[key].net += Number(item.netAmount || 0)
        grouped[key].vatAmount += Number(item.vatAmount || 0)
        grouped[key].gross += Number(item.grossAmount || 0)
      })

      return Object.values(grouped)
    },

    translations() {
      return {
        hu: {
          /*preview: 'Előnézet',
          advanceItem: 'Előleg levonás',
          seller: 'Eladó',
          buyer: 'Vevő',
          type: 'Típus',
          issueDate: 'Kiállítás dátuma',
          fulfillmentDate: 'Teljesítés dátuma',
          paymentDeadline: 'Fizetési határidő',
          paymentMethod: 'Fizetési mód',
          orderNumber: 'Rendelésszám',
          relatedProforma: 'Kapcsolódó díjbekérő',
          currency: 'Pénznem',
          taxMode: 'Adózás',
          itemName: 'Megnevezés',
          quantity: 'Mennyiség',
          unit: 'Egység',
          unitPriceNet: 'Egységár (nettó)',
          vat: 'ÁFA',
          net: 'Nettó',
          vatAmount: 'ÁFA összeg',
          gross: 'Bruttó',
          noItems: 'Nincs megjeleníthető tétel',
          vatSummary: 'ÁFA összesítő',
          totalNet: 'Nettó összesen',
          totalVat: 'ÁFA összesen',
          totalGross: 'Bruttó végösszeg',
          note: 'Megjegyzés',
          taxNumber: 'Adószám',
          email: 'Email',
          phone: 'Telefon',
          bankAccount: 'Bankszámla',
          reference: 'Cikkszám',
          vatExempt: 'Adómentes',
          proformaPreview: 'Díjbekérő előnézet',
          invoicePreview: 'Számla előnézet',
          finalInvoicePreview: 'Végszámla előnézet',
          piece: 'db',*/

          preview: 'Preview',
          advanceItem: 'Advance deduction',
          seller: 'Seller',
          buyer: 'Buyer',
          type: 'Type',
          issueDate: 'Issue date',
          fulfillmentDate: 'Completion date',
          paymentDeadline: 'Payment due date',
          paymentMethod: 'Payment method',
          orderNumber: 'Order number',
          relatedProforma: 'Related proforma',
          currency: 'Currency',
          taxMode: 'Tax mode',
          itemName: 'Item',
          quantity: 'Quantity',
          unit: 'Unit',
          unitPriceNet: 'Unit price (net)',
          vat: 'VAT',
          net: 'Net',
          vatAmount: 'VAT amount',
          gross: 'Gross',
          noItems: 'No items to display',
          vatSummary: 'VAT summary',
          subtotalNet: 'Subtotal net',
          discount: 'Discount',
          totalNet: 'Total net',
          totalVat: 'Total VAT',
          totalGross: 'Total gross',
          note: 'Comment',
          taxNumber: 'Tax number',
          email: 'Email',
          phone: 'Phone',
          bankName: 'Bank',
          bankAccount: 'Bank account',
          iban: 'IBAN',
          reference: 'Reference',
          vatExempt: 'VAT exempt',
          proformaPreview: 'Proforma preview',
          depositInvoicePreview: 'Deposit invoice preview',
          invoicePreview: 'Invoice preview',
          finalInvoicePreview: 'Final invoice preview',
          piece: 'pcs',
        },
        en: {
          preview: 'Preview',
          advanceItem: 'Advance deduction',
          seller: 'Seller',
          buyer: 'Buyer',
          type: 'Type',
          issueDate: 'Issue date',
          fulfillmentDate: 'Completion date',
          paymentDeadline: 'Payment due date',
          paymentMethod: 'Payment method',
          orderNumber: 'Order number',
          relatedProforma: 'Related proforma',
          currency: 'Currency',
          taxMode: 'Tax mode',
          itemName: 'Item',
          quantity: 'Quantity',
          unit: 'Unit',
          unitPriceNet: 'Unit price (net)',
          vat: 'VAT',
          net: 'Net',
          vatAmount: 'VAT amount',
          gross: 'Gross',
          noItems: 'No items to display',
          vatSummary: 'VAT summary',
          subtotalNet: 'Subtotal net',
          discount: 'Discount',
          totalNet: 'Total net',
          totalVat: 'Total VAT',
          totalGross: 'Total gross',
          note: 'Comment',
          taxNumber: 'Tax number',
          email: 'Email',
          phone: 'Phone',
          bankName: 'Bank',
          bankAccount: 'Bank account',
          iban: 'IBAN',
          reference: 'Reference',
          vatExempt: 'VAT exempt',
          proformaPreview: 'Proforma preview',
          depositInvoicePreview: 'Deposit invoice preview',
          invoicePreview: 'Invoice preview',
          finalInvoicePreview: 'Final invoice preview',
          piece: 'pcs',
        },
        ro: {
          preview: 'Previzualizare',
          advanceItem: 'Scădere avans',
          seller: 'Furnizor',
          buyer: 'Cumpărător',
          type: 'Tip',
          issueDate: 'Data emiterii',
          fulfillmentDate: 'Data livrării',
          paymentDeadline: 'Scadență',
          paymentMethod: 'Metoda de plată',
          orderNumber: 'Număr comandă',
          relatedProforma: 'Proformă asociată',
          currency: 'Monedă',
          taxMode: 'Regim fiscal',
          itemName: 'Produs',
          quantity: 'Cantitate',
          unit: 'UM',
          unitPriceNet: 'Preț unitar (net)',
          vat: 'TVA',
          net: 'Net',
          vatAmount: 'Valoare TVA',
          gross: 'Brut',
          noItems: 'Nu există articole de afișat',
          vatSummary: 'Centralizare TVA',
          subtotalNet: 'Subtotal net',
          discount: 'Discount',
          totalNet: 'Total net',
          totalVat: 'Total TVA',
          totalGross: 'Total brut',
          note: 'Observații',
          taxNumber: 'Cod fiscal',
          email: 'Email',
          phone: 'Telefon',
          bankName: 'Bancă',
          bankAccount: 'Cont bancar',
          iban: 'IBAN',
          reference: 'Cod produs',
          vatExempt: 'scutit de TVA',
          proformaPreview: 'Previzualizare proformă',
          depositInvoicePreview: 'Previzualizare factură de depozit',
          invoicePreview: 'Previzualizare factură',
          finalInvoicePreview: 'Previzualizare factură finală',
          piece: 'buc',
        },
      }
    },
  },

  methods: {
    getTodayDate() {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    toNumber(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }

      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0
      }

      const normalized = String(value).replace(',', '.').trim()
      const parsed = Number(normalized)

      return Number.isNaN(parsed) ? 0 : parsed
    },

    isAlmostEqual(a, b) {
      return Math.abs(Number(a || 0) - Number(b || 0)) < 0.0001
    },

    roundMoney(value) {
      return Math.round(this.toNumber(value) * 100) / 100
    },

    getLocaleCode() {
      if (this.currentLocale === 'en') return 'en-GB'
      if (this.currentLocale === 'ro') return 'ro-RO'
      return 'hu-HU'
    },

    getTranslationValue(translations, key) {
      if (!translations || typeof translations !== 'object') {
        return null
      }

      const localeBlock =
        translations[this.currentLocale] ||
        translations.en ||
        translations.hu ||
        translations.ro ||
        null

      if (!localeBlock || typeof localeBlock !== 'object') {
        return null
      }

      return localeBlock[key] || null
    },

    resolveCustomFields(item) {
      const customFields = item?.custom_fields ?? item?.item?.custom_fields ?? null

      if (!customFields) {
        return {}
      }

      if (typeof customFields === 'object') {
        return customFields
      }

      try {
        return JSON.parse(customFields) || {}
      } catch (e) {
        return {}
      }
    },

    resolveItemComment(item, quantity) {
      const explicitComment =
        item.comment ??
        item.public_notes ??
        item.note ??
        null
      const comments = []

      if (explicitComment) {
        comments.push(String(explicitComment).trim())
      }

      const customFields = this.resolveCustomFields(item)
      const customsTariffNumber = customFields.customs_tariff_number || null
      const unitWeight = this.toNumber(
        item.alt_unit_weight ??
        item.unit_weight ??
        item.unitWeight ??
        item.item?.unit_weight ??
        item.item?.unitWeight ??
        0
      )
      const totalWeight = unitWeight > 0 && quantity > 0
        ? Math.round(unitWeight * quantity * 1000) / 1000
        : null
      const existingCommentText = comments.join('\n').toLowerCase()

      if (customsTariffNumber && !existingCommentText.includes('vámtarifaszám')) {
        comments.push(`Vámtarifaszám: ${customsTariffNumber}`)
      }

      if (totalWeight !== null && !existingCommentText.includes('súly:')) {
        comments.push(`Súly: ${this.formatWeight(totalWeight)} kg`)
      }

      return comments.length ? comments.filter(Boolean).join('\n') : null
    },

    resolveItemName(item) {
      return (
        item.alt_name ||
        this.getTranslationValue(item.translations, 'name') ||
        this.getTranslationValue(item.translations, 'web_name_1') ||
        this.getTranslationValue(item.item?.translations, 'name') ||
        this.getTranslationValue(item.item?.translations, 'web_name_1') ||
        item.name ||
        item.item?.name ||
        item.item?.alt_name ||
        item.reference ||
        'Tétel'
      )
    },

    resolveQuantity(item) {
      return this.toNumber(
        item.quantity ??
        item.qty ??
        item.num_of_pcs ??
        1
      )
    },

    resolveUnit(item) {
      if (item.quantityUnit) {
        return item.quantityUnit
      }

      if (item.quantity_unit?.name) {
        return item.quantity_unit.name
      }

      const translatedQuantityUnitName =
        this.getTranslationValue(item.quantity_unit?.translations, 'name')

      if (translatedQuantityUnitName) {
        return translatedQuantityUnitName
      }

      if (item.unit) {
        return item.unit
      }

      return this.t.piece
    },

    resolveVat(item) {
      return (
        item.vat_name ||
        item.vatName ||
        item.vat_value ||
        item.vatValue ||
        item.vat ||
        item.vatRate ||
        item.vat_rate ||
        null
      )
    },

    resolveEffectiveVat(item) {
      const currency = String(this.documentCurrency || '').toUpperCase()

      if (this.hasZeroTaxOrder) {
        const storedVatName = String(item?.vat_name || item?.vatName || '').trim().toUpperCase()

        if (storedVatName && Number.isNaN(Number(storedVatName))) {
          return storedVatName
        }

        return currency === 'EUR' ? 'EUT' : 'F.AFA'
      }

      const isService = item?.service === true || item?.service === 1 || item?.item?.service === true || item?.item?.service === 1

      if (this.hasReverseTaxOrder && !isService) {
        return 'F.AFA'
      }

      return this.resolveVat(item) || this.settings?.tax || this.settings?.vat || this.order?.priceList?.vat_value || (currency === 'EUR' ? 'EUT' : 27)
    },

    isVatExempt(vat) {
      const normalized = String(vat || '').trim().toUpperCase()

      return [
        'TAHK', 'TAM', 'AAM', 'EUT', 'EUKT', 'F.AFA', 'K.AFA', 'HO',
        'EUE', 'EUFADE', 'EUFAD37', 'ATK', 'NAM', 'EAM', 'KBAUK', 'KBAET',
      ].includes(normalized)
    },

    resolveVatDisplay(vat, vatExempt) {
      if (vatExempt) {
        const normalized = String(vat || '').trim().toUpperCase()

        if (normalized) {
          return `${normalized} (${this.t.vatExempt})`
        }

        return `0% (${this.t.vatExempt})`
      }

      const numericVat = Number(vat)

      if (!Number.isNaN(numericVat) && String(vat).trim() !== '') {
        return `${numericVat}%`
      }

      return String(vat || '-')
    },

    resolveLineNet(item, quantity) {
      const explicitNet = item.netValue ?? item.netAmount ?? item.net_amount

      if (explicitNet !== undefined && explicitNet !== null && explicitNet !== '') {
        return this.toNumber(explicitNet)
      }

      const explicitGross = item.grossValue ?? item.grossAmount ?? item.gross_amount
      const explicitTax = item.tax ?? item.vatAmount ?? item.vat_amount

      if (
        explicitGross !== undefined &&
        explicitGross !== null &&
        explicitTax !== undefined &&
        explicitTax !== null
      ) {
        return this.toNumber(explicitGross) - this.toNumber(explicitTax)
      }

      const priceNet = this.toNumber(
        item.unitPriceNet ??
        item.unit_price_net ??
        item.unit_net_price ??
        item.price_net ??
        item.net_price ??
        0
      )

      if (quantity > 0) {
        return priceNet * quantity
      }

      return priceNet
    },

    resolveLineVat(item, netAmount, vat) {
      const explicitTax = item.tax ?? item.vatAmount ?? item.vat_amount

      if (explicitTax !== undefined && explicitTax !== null && explicitTax !== '') {
        return this.toNumber(explicitTax)
      }

      return this.calculateVatAmount(netAmount, vat)
    },

    resolveLineGross(item, netAmount, vatAmount) {
      const explicitGross = item.grossValue ?? item.grossAmount ?? item.gross_amount

      if (explicitGross !== undefined && explicitGross !== null && explicitGross !== '') {
        return this.toNumber(explicitGross)
      }

      const priceGross = this.toNumber(
        item.unitPriceGross ??
        item.unit_price_gross ??
        item.price_gross ??
        item.gross_price ??
        0
      )

      if (priceGross > 0 && this.toNumber(item.quantity) > 0) {
        const quantity = this.toNumber(item.quantity)

        if (this.isAlmostEqual(priceGross * quantity, netAmount + vatAmount)) {
          return priceGross * quantity
        }
      }

      return Number(netAmount || 0) + Number(vatAmount || 0)
    },

    resolveUnitPriceNet(item, quantity, netAmount) {
      const explicitUnitNet = item.unitPriceNet ?? item.unit_price_net ?? item.unit_net_price

      if (explicitUnitNet !== undefined && explicitUnitNet !== null && explicitUnitNet !== '') {
        return this.toNumber(explicitUnitNet)
      }

      const priceNet = item.price_net ?? item.net_price

      if (priceNet !== undefined && priceNet !== null && priceNet !== '') {
        const parsedPriceNet = this.toNumber(priceNet)

        if (quantity > 0) {
          if (this.isAlmostEqual(parsedPriceNet * quantity, netAmount)) {
            return parsedPriceNet
          }

          if (this.isAlmostEqual(parsedPriceNet, netAmount)) {
            return netAmount / quantity
          }
        }

        return parsedPriceNet
      }

      if (quantity > 0) {
        return netAmount / quantity
      }

      return netAmount
    },

    buildProformaItems() {
      const type = this.settings?.type || 'Előleg'
      const mode = this.settings?.mode || 'percent'
      const includeOrderItems = Boolean(this.settings?.include_order_items)

      let targetNetAmount = 0

      if (mode === 'percent') {
        const percent = this.toNumber(this.settings?.percent || 0)
        targetNetAmount = this.orderNetTotal * (percent / 100)
      }

      if (mode === 'value') {
        targetNetAmount = this.toNumber(this.settings?.value || 0)
      }

      if (includeOrderItems && this.normalizedOrderItems.length) {
        const rawBaseTotal = this.normalizedOrderItems.reduce(
          (sum, item) => sum + this.toNumber(item.netAmount),
          0
        )
        const useEqualWeights = Math.abs(rawBaseTotal) < 0.0000001
        const baseTotal = useEqualWeights
          ? this.normalizedOrderItems.length
          : rawBaseTotal

        let allocatedNet = 0

        return this.normalizedOrderItems.map((item, index) => {
          const isLast = index === this.normalizedOrderItems.length - 1
          const itemBaseNet = this.toNumber(item.netAmount)
          const weight = useEqualWeights
            ? 1 / this.normalizedOrderItems.length
            : itemBaseNet / baseTotal
          const netAmount = this.roundMoney(
            isLast
              ? targetNetAmount - allocatedNet
              : targetNetAmount * weight
          )

          if (!isLast) {
            allocatedNet += netAmount
          }

          const vat = String(item.vat || '27').trim().toUpperCase()
          const vatExempt = this.isVatExempt(vat)
          const vatAmount = this.roundMoney(
            vatExempt ? 0 : this.calculateVatAmount(netAmount, vat)
          )
          const grossAmount = this.roundMoney(netAmount + vatAmount)
          const quantity = this.toNumber(item.quantity) || 1

          return {
            ...item,
            quantity,
            unitPriceNet: quantity > 0
              ? this.roundMoney(netAmount / quantity)
              : netAmount,
            vat,
            vatDisplay: this.resolveVatDisplay(vat, vatExempt),
            netAmount,
            vatAmount,
            grossAmount,
          }
        })
      }

      const vatGroups = this.normalizedOrderItems.reduce((groups, item) => {
        const vat = String(item.vat || '27').trim().toUpperCase()

        if (!groups[vat]) {
          groups[vat] = 0
        }

        groups[vat] += this.toNumber(item.netAmount)
        return groups
      }, {})
      const groupedVatCodes = Object.keys(vatGroups)

      if (!includeOrderItems && groupedVatCodes.length > 1) {
        const groupTotal = groupedVatCodes.reduce((sum, vat) => sum + this.toNumber(vatGroups[vat]), 0)
        const safeGroupTotal = Math.abs(groupTotal) < 0.0000001 ? groupedVatCodes.length : groupTotal
        const suffix = mode === 'percent'
          ? `(${this.toNumber(this.settings?.percent || 0)}%)`
          : ''
        let allocatedNet = 0

        return groupedVatCodes.map((vat, index) => {
          const isLast = index === groupedVatCodes.length - 1
          const weight = Math.abs(groupTotal) < 0.0000001
            ? 1 / groupedVatCodes.length
            : this.toNumber(vatGroups[vat]) / safeGroupTotal
          const netAmount = this.roundMoney(
            isLast
              ? targetNetAmount - allocatedNet
              : targetNetAmount * weight
          )

          if (!isLast) {
            allocatedNet += netAmount
          }

          const vatExempt = this.isVatExempt(vat)
          const vatAmount = this.roundMoney(
            vatExempt ? 0 : this.calculateVatAmount(netAmount, vat)
          )
          const grossAmount = this.roundMoney(netAmount + vatAmount)

          return {
            name: `${type} ${suffix}`.trim(),
            note: this.settings?.note || null,
            reference: null,
            quantity: 1,
            unit: this.t.piece,
            unitPriceNet: netAmount,
            vat,
            vatDisplay: this.resolveVatDisplay(vat, vatExempt),
            netAmount,
            vatAmount,
            grossAmount,
          }
        })
      }

      const vat = String(
        this.settings?.vat ||
        this.settings?.tax ||
        groupedVatCodes[0] ||
        27
      )
        .trim()
        .toUpperCase()
      const vatExempt = this.isVatExempt(vat)
      const vatAmount = this.roundMoney(
        vatExempt ? 0 : this.calculateVatAmount(targetNetAmount, vat)
      )
      const grossAmount = this.roundMoney(targetNetAmount + vatAmount)
      const suffix = mode === 'percent'
        ? `(${this.toNumber(this.settings?.percent || 0)}%)`
        : ''

      return [{
        name: `${type} ${suffix}`.trim(),
        note: this.settings?.note || null,
        reference: null,
        quantity: 1,
        unit: this.t.piece,
        unitPriceNet: targetNetAmount,
        vat,
        vatDisplay: this.resolveVatDisplay(vat, vatExempt),
        netAmount: targetNetAmount,
        vatAmount,
        grossAmount,
      }]
    },

    calculateVatAmount(netAmount, vat) {
      const numericVat = Number(vat)

      if (Number.isNaN(numericVat) || String(vat).trim() === '') {
        return 0
      }

      return Number(netAmount || 0) * (numericVat / 100)
    },

    formatDate(value) {
      if (!value) return '-'

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return new Intl.DateTimeFormat(this.getLocaleCode()).format(date)
    },

    formatMoney(value) {
      return (
        new Intl.NumberFormat(this.getLocaleCode(), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value || 0)) +
        ' ' +
        this.documentCurrency
      )
    },

    formatQuantity(value) {
      return new Intl.NumberFormat(this.getLocaleCode(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value || 0))
    },

    formatWeight(value) {
      return new Intl.NumberFormat(this.getLocaleCode(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      }).format(Number(value || 0))
    },
    normalizePaymentMethodKey(method) {
      const map = {
        'Készpénz': 'cash',
        'Cash': 'cash',
        'Numerar': 'cash',
      
        'Átutalás': 'transfer',
        'Bank transfer': 'transfer',
        'Transfer bancar': 'transfer',
      
        'Bankkártya': 'card',
        'Credit card': 'card',
        'Card bancar': 'card',

        'Utánvét': 'cash_on_delivery',
        'Simplepay': 'simplepay',
      };
    
      return map[method] ?? method;
    },

    resolveDocumentPaymentMethod() {
      if (
        this.settings?.paymentMethod !== undefined &&
        this.settings?.paymentMethod !== null &&
        this.settings?.paymentMethod !== ''
      ) {
        return this.settings.paymentMethod
      }

      if (this.documentType === 'final_invoice') {
        const depositInvoices = (this.order?.documents ?? [])
          .filter(document => document?.type === 'deposit_invoice')

        if (depositInvoices.length) {
          const lastDepositInvoice = depositInvoices[depositInvoices.length - 1]

          if (
            lastDepositInvoice?.payment_method !== undefined &&
            lastDepositInvoice?.payment_method !== null &&
            lastDepositInvoice?.payment_method !== ''
          ) {
            return lastDepositInvoice.payment_method
          }
        }
      }

      return this.order?.payment_method ?? null
    },

    isBankTransferPaymentMethod() {
      const paymentMethod = this.resolveDocumentPaymentMethod()

      if (Number(paymentMethod) === 2) {
        return true
      }

      const normalized = String(
        this.normalizePaymentMethodKey(paymentMethod) ?? ''
      ).trim().toLowerCase()

      return [
        'transfer',
        'átutalás',
        'atutalas',
        'bank transfer',
        'transfer bancar',
      ].includes(normalized)
    },

    getLatePaymentNotice() {
      return 'Késedelmes fizetés esetén Társaságunk jogosult a szerződésben, illetve az ÁSZF-ben foglaltak szerint, ennek hiányában a jogszabály szerinti késedelmi kamat, valamint – jogszabályi feltételek fennállása esetén – a behajtási költségátalány érvényesítésére. ÁSZF: www.kismetal.hu/aszf.'
    },

    shouldShowLatePaymentNotice() {
      if (!['invoice', 'final_invoice'].includes(this.documentType)) {
        return false
      }

      if (this.isCurrentDocumentPaid() || !this.isBankTransferPaymentMethod()) {
        return false
      }

      const grossAmount = this.roundMoney(this.totalGross)

      if (this.documentType === 'final_invoice') {
        // A backend 10 Ft vagy annál kisebb végösszeget nullás végszámlaként kezel.
        return grossAmount > 10
      }

      return Math.abs(grossAmount) >= 0.01
    },

    normalizePaymentMethod(lang, method) {
      const key = this.normalizePaymentMethodKey(method);

      const methods = {
        1: {
          hu: 'Készpénz',
          en: 'Cash',
          ro: 'Numerar',
        },
        2: {
          hu: 'Átutalás',
          en: 'Bank transfer',
          ro: 'Transfer bancar',
        },
        3: {
          hu: 'Simplepay',
          en: 'Simplepay',
          ro: 'Simplepay',
        },
        4: {
          hu: 'Utánvét',
        },
        5: {
          hu: 'Bankkártya',
          en: 'Credit card',
          ro: 'Card bancar',
        },
      };
    
      return methods[key]?.[lang] ?? methods[key]?.hu ?? method;
    },

    resolvePaidAdvanceTotal() {
      const directAmount = this.toNumber(
        this.order?.paidAdvanceTotal ??
        this.order?.paid_advance_total ??
        this.order?.paidPrepaymentTotal ??
        this.order?.paid_prepayment_total ??
        this.order?.paidProformaTotal ??
        this.order?.paid_proforma_total ??
        this.order?.advancePaidTotal ??
        this.order?.advance_paid_total ??
        this.order?.prepaid_total ??
        this.order?.prepaidTotal ??
        this.order?.paid_proforma_amount ??
        this.order?.paidProformaAmount ??
        0
      )

      if (directAmount > 0) {
        return directAmount
      }
    
      const possibleArrays = [
        this.order?.documents,
        this.order?.paid_proformas,
        this.order?.paidProformas,
        this.order?.proformas,
        this.order?.prepayments,
        this.order?.advances,
      ]


    
      for (const list of possibleArrays) {
        if (!Array.isArray(list) || !list.length) {
          continue
        }
      
        const sum = list.reduce((total, item) => {
          const isPaid =
            item?.paid === true ||
            item?.paid === 1 ||
            item?.is_paid === true ||
            item?.is_paid === 1 ||
            item?.status === 'paid' ||
            item?.payment_status === 'paid'

          const isDeposit = item?.type === 'deposit_invoice' || item?.document_type === 'deposit_invoice';
        
          if (!isPaid || !isDeposit) {
            return total
          }

        
          return total + this.toNumber(
            item?.paid_gross_amount ??
            item?.gross_amount ??
            item?.grossAmount ??
            item?.amount ??
            item?.total ??
            item?.value ??
            item?.paid_amount ??
            item?.paidAmount ??
            0
          )
        }, 0)
      
        if (sum > 0) {
          return sum
        }
      }
    
      return 0
    },

    buildFinalInvoiceItems() {
      const items = [...this.normalizedOrderItems]
      const depositDocuments = this.resolvePaidDepositDocuments()

      if (depositDocuments.length) {
        depositDocuments.forEach(document => {
          const breakdown = this.getVatBreakdownFromDocument(document)

          breakdown.forEach(line => {
            const vat = String(line.tax_rate || line.vat || '27').trim().toUpperCase()
            const vatExempt = this.isVatExempt(vat)

            items.push({
              name: `${this.t.advanceItem}${document.document_number ? ' - ' + document.document_number : ''}`,
              note: null,
              reference: null,
              quantity: 1,
              unit: this.t.piece,
              unitPriceNet: -this.toNumber(line.net_amount),
              vat,
              vatDisplay: this.resolveVatDisplay(vat, vatExempt),
              netAmount: -this.toNumber(line.net_amount),
              vatAmount: -this.toNumber(line.vat_amount),
              grossAmount: -this.toNumber(line.gross_amount),
              isAdvanceDeduction: true,
            })
          })
        })

        return items
      }

      const paidAdvanceTotal = this.resolvePaidAdvanceTotal()

      if (paidAdvanceTotal <= 0) {
        return items
      }

      const fallbackVat = String(
        this.normalizedOrderItems[0]?.vat ||
        this.settings?.tax ||
        this.settings?.vat ||
        this.order?.priceList?.vat_value ||
        27
      ).trim().toUpperCase()
      const vatExempt = this.isVatExempt(fallbackVat)

      let advanceNet = -paidAdvanceTotal
      let advanceVat = 0
      let advanceGross = -paidAdvanceTotal

      if (!vatExempt) {
        const numericVat = Number(fallbackVat)

        if (!Number.isNaN(numericVat) && numericVat >= 0) {
          advanceNet = -(paidAdvanceTotal / (1 + numericVat / 100))
          advanceVat = advanceGross - advanceNet
        }
      }

      items.push({
        name: this.t.advanceItem,
        note: null,
        reference: null,
        quantity: 1,
        unit: this.t.piece,
        unitPriceNet: advanceNet,
        vat: fallbackVat,
        vatDisplay: this.resolveVatDisplay(fallbackVat, vatExempt),
        netAmount: advanceNet,
        vatAmount: advanceVat,
        grossAmount: advanceGross,
        isAdvanceDeduction: true,
      })

      return items
    },

    resolvePaidDepositDocuments() {
      const possibleArrays = [
        this.order?.documents,
        this.order?.generatedDocuments,
        this.order?.generated_documents,
        this.order?.advances,
        this.order?.prepayments,
      ]

      for (const list of possibleArrays) {
        if (!Array.isArray(list)) {
          continue
        }

        const documents = list.filter(document => {
          const type = String(document?.type || document?.document_type || '').toLowerCase()
          const status = String(document?.status || document?.payment_status || '').toLowerCase()
          const reverseStatus = String(document?.reverse_status || '').toLowerCase()
          const isPaid = status === 'paid' || document?.paid === true || document?.paid === 1
          const isReversed = status === 'reversed' || reverseStatus === 'reversed' || !!document?.reversed_by_document_id

          return type === 'deposit_invoice' && isPaid && !isReversed
        })

        if (documents.length) {
          return documents
        }
      }

      return []
    },

    normalizeAdvanceVatBreakdownLine(line, fallbackTaxRate = '27') {
      const taxRate = String(
        line?.tax_rate ??
        line?.vat ??
        fallbackTaxRate ??
        '27'
      ).trim().toUpperCase()

      const normalizedTaxRate = taxRate === 'MIXED' ? '27' : taxRate
      const netAmount = this.roundMoney(
        this.toNumber(line?.net_amount ?? line?.net)
      )

      const hasVatAmount =
        line?.vat_amount !== null &&
        line?.vat_amount !== undefined &&
        line?.vat_amount !== ''

      const hasGrossAmount =
        line?.gross_amount !== null &&
        line?.gross_amount !== undefined &&
        line?.gross_amount !== '' ||
        line?.gross !== null &&
        line?.gross !== undefined &&
        line?.gross !== ''

      let vatAmount = this.roundMoney(
        this.toNumber(line?.vat_amount)
      )

      let grossAmount = this.roundMoney(
        this.toNumber(line?.gross_amount ?? line?.gross)
      )

      /*
       * Régi előlegszámláknál a vat_amount nincs mindig eltárolva.
       * Ha a bruttó rendelkezésre áll, az áfát a bruttó és nettó
       * különbségéből állítjuk elő. Ugyanezt tesszük akkor is,
       * ha a kapott három érték matematikailag nem egyezik.
       */
      if (
        hasGrossAmount &&
        (
          !hasVatAmount ||
          !this.isAlmostEqual(netAmount + vatAmount, grossAmount)
        )
      ) {
        vatAmount = this.roundMoney(grossAmount - netAmount)
      }

      /*
       * Ha bruttó sincs eltárolva, numerikus áfakulcs esetén
       * a nettóból számoljuk ki az áfát. Adómentes kulcsoknál 0 marad.
       */
      if (!hasGrossAmount) {
        if (!this.isVatExempt(normalizedTaxRate)) {
          const numericVat = Number(normalizedTaxRate)

          if (!Number.isNaN(numericVat) && numericVat >= 0) {
            vatAmount = this.roundMoney(netAmount * (numericVat / 100))
          }
        } else {
          vatAmount = 0
        }

        grossAmount = this.roundMoney(netAmount + vatAmount)
      }

      // A preview-ban is mindig teljesüljön: bruttó = nettó + áfa.
      grossAmount = this.roundMoney(netAmount + vatAmount)

      return {
        tax_rate: normalizedTaxRate,
        net_amount: netAmount,
        vat_amount: vatAmount,
        gross_amount: grossAmount,
      }
    },

    getVatBreakdownFromDocument(document) {
      let payload = document?.extra_payload || {}

      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (error) {
          payload = {}
        }
      }

      const fallbackTaxRate = String(
        document?.tax_rate || '27'
      ).trim().toUpperCase()

      if (Array.isArray(payload?.vat_breakdown) && payload.vat_breakdown.length) {
        return payload.vat_breakdown.map(line =>
          this.normalizeAdvanceVatBreakdownLine(line, fallbackTaxRate)
        )
      }

      return [
        this.normalizeAdvanceVatBreakdownLine({
          tax_rate: fallbackTaxRate,
          net_amount: document?.net_amount,
          vat_amount: document?.vat_amount,
          gross_amount:
            document?.gross_amount ??
            document?.paid_gross_amount,
        }, fallbackTaxRate),
      ]
    },

    isTruthyPaidValue(value) {
      return (
        value === true ||
        value === 1 ||
        value === '1' ||
        String(value || '').toLowerCase() === 'paid'
      )
    },

    isCurrentDocumentPaid() {
      const paidCandidates = [
        this.settings?.paid,
        this.settings?.is_paid,
        this.settings?.isPaid,
        this.settings?.payment_status,
        this.settings?.paymentStatus,
        this.order?.paid,
        this.order?.is_paid,
        this.order?.isPaid,
        this.order?.payment_status,
        this.order?.paymentStatus,
      ]

      return paidCandidates.some(value => this.isTruthyPaidValue(value))
    },

    shouldShowNoPaymentRequiredPhrase() {
      if (this.documentType === 'deposit_invoice') {
        return true
      }

      if (this.documentType !== 'final_invoice') {
        return false
      }

      const finalGrossTotal = this.roundMoney(this.totalGross)

      return finalGrossTotal <= 10 || this.isCurrentDocumentPaid()
    },

    resolveComments() {
      const noPaymentRequiredPhrase = 'A számla pénzügyi teljesítést nem igényel.'
      const latePaymentNotice = this.getLatePaymentNotice()
      const comments = []

      if (['deposit_invoice', 'final_invoice'].includes(this.documentType)) {
        const depositComments = (this.order?.documents ?? [])
          .filter(document => document?.type === 'deposit_invoice' && document?.comment)
          .map(document => String(document.comment).trim())
          .filter(Boolean)

        depositComments.forEach(comment => {
          const cleanedComment = comment
            .split(noPaymentRequiredPhrase).join('')
            .split(latePaymentNotice).join('')
            .trim()

          if (cleanedComment) {
            comments.push(cleanedComment)
          }
        })
      }

      if (this.settings?.note) {
        comments.push(String(this.settings.note).trim())
      }

      if (this.shouldShowNoPaymentRequiredPhrase()) {
        comments.push(noPaymentRequiredPhrase)
      }

      if (this.shouldShowLatePaymentNotice()) {
        comments.push(latePaymentNotice)
      }

      const uniqueComments = [...new Set(comments.filter(Boolean))]
      const finalComment = uniqueComments.join('\n')

      return finalComment || null
    },
  },
}
</script>

<style scoped>
.invoice-preview-panel {
  height: 100%;
}

.preview-sheet {
  background: #fafafa;
}

.preview-block {
  min-width: 220px;
}

.preview-meta {
  min-width: 260px;
}

.totals-box {
  min-width: 320px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
}

.note-box {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  white-space: pre-line;
}

.item-comment {
  white-space: pre-line;
}
.advance-deduction-row {
  background: #fff8f0;
  font-weight: 600;
}

.discount-row {
  color: #2e7d32;
}
</style>